// Deduction preview + manual push. Shows the diff between Administr-computed
// per-pay-period amounts and what's currently in the payroll vendor; nothing
// is written until a human clicks Push.

import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowRight, RefreshCw, Send, DollarSign } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { PayrollConnection } from "./PayrollConnectionCard";

interface DiffItem {
  vendorEmployeeId: string;
  employeeId: number | null;
  lineOfCoverage: string;
  action: "create" | "update" | "remove" | "none";
  current?: { employeeDeduction: number; companyContribution?: number };
  desired?: {
    employeeDeduction: number;
    companyContribution: number;
    payFrequency: string;
    assumedPayFrequency: boolean;
  };
}

interface Preview {
  items: DiffItem[];
  skipped: Array<{ employeeId: number; reason: string; lineOfCoverage?: string }>;
  summary: { create: number; update: number; remove: number; none: number; skipped: number };
  employees: Record<number, { firstName: string; lastName: string }>;
  lineLabels: Record<string, string>;
}

interface SyncRun {
  id: number;
  kind: string;
  status: string;
  completedAt: string | null;
  recordsCreated: number | null;
  recordsUpdated: number | null;
  recordsFailed: number | null;
}

const ACTION_BADGES: Record<DiffItem["action"], { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  create: { label: "New", variant: "default" },
  update: { label: "Change", variant: "secondary" },
  remove: { label: "Remove", variant: "destructive" },
  none: { label: "In sync", variant: "outline" },
};

const money = (n: number | undefined) => (n == null ? "—" : `$${n.toFixed(2)}`);

export default function PayrollDeductionsPanel({
  connection,
}: {
  connection: PayrollConnection;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pushRunId, setPushRunId] = useState<number | null>(null);

  const previewKey = `/api/payroll-connections/${connection.id}/deductions/preview`;
  const runsKey = `/api/payroll-connections/${connection.id}/sync-runs`;

  const { data: preview, isLoading, isFetching, refetch } = useQuery<Preview>({
    queryKey: [previewKey],
    staleTime: 60_000,
  });

  const { data: runs = [] } = useQuery<SyncRun[]>({
    queryKey: [runsKey],
    refetchInterval: pushRunId != null ? 3000 : false,
  });
  const pushRun = pushRunId != null ? runs.find((r) => r.id === pushRunId) : undefined;
  const lastPush = runs.find((r) => r.kind === "deductions_push" && r.status !== "running");

  // When the polled push run settles, report + refresh the preview.
  useEffect(() => {
    if (!pushRun || pushRun.status === "running") return;
    setPushRunId(null);
    const failed = pushRun.recordsFailed ?? 0;
    toast(
      failed > 0
        ? {
            variant: "destructive",
            title: `Push finished with ${failed} failure${failed === 1 ? "" : "s"}`,
            description: `${pushRun.recordsCreated ?? 0} added, ${pushRun.recordsUpdated ?? 0} updated. See sync history for details.`,
          }
        : {
            variant: "success",
            title: "Deductions pushed",
            description: `${pushRun.recordsCreated ?? 0} added, ${pushRun.recordsUpdated ?? 0} updated.`,
          },
    );
    queryClient.invalidateQueries({ queryKey: [previewKey] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushRun?.status]);

  const pushMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/payroll-connections/${connection.id}/deductions/push`, {});
      return (await res.json()) as { runId: number };
    },
    onSuccess: ({ runId }) => {
      setPushRunId(runId);
      queryClient.invalidateQueries({ queryKey: [runsKey] });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Push failed to start", description: e?.message }),
  });

  const items = preview?.items ?? [];
  const changes = items.filter((i) => i.action !== "none");
  const summary = preview?.summary;
  const pushing = pushMutation.isPending || pushRunId != null;

  // Group items by employee for display
  const byEmployee = new Map<string, { name: string; items: DiffItem[] }>();
  for (const item of items) {
    const emp = item.employeeId != null ? preview?.employees[item.employeeId] : undefined;
    const name = emp ? `${emp.firstName} ${emp.lastName}` : `Payroll employee ${item.vendorEmployeeId.slice(0, 8)}…`;
    const key = String(item.employeeId ?? item.vendorEmployeeId);
    const group = byEmployee.get(key) ?? { name, items: [] };
    group.items.push(item);
    byEmployee.set(key, group);
  }

  const unlinkedSkips = (preview?.skipped ?? []).filter((s) => s.reason === "unlinked");

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Payroll deductions
          </CardTitle>
          <CardDescription className="mt-1">
            Per-paycheck benefit deductions computed from enrollments, compared against{" "}
            {connection.vendor.name}. Employee-paid medical, dental, vision and FSAs push pre-tax;
            life and disability push post-tax. Nothing changes until you push.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching || pushing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={pushing || isLoading || changes.length === 0}
          >
            <Send className="h-4 w-4 mr-2" />
            {pushing ? "Pushing…" : `Push to ${connection.vendor.name}`}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Computing deductions…</p>
        ) : items.length === 0 && unlinkedSkips.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No active benefit enrollments to push for linked employees.
          </p>
        ) : (
          <div className="space-y-4">
            {summary && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant={changes.length > 0 ? "default" : "secondary"}>
                  {summary.create} new · {summary.update} changed · {summary.remove} to remove
                </Badge>
                <Badge variant="outline">{summary.none} in sync</Badge>
                {lastPush?.completedAt && (
                  <span className="text-muted-foreground ml-auto">
                    Last pushed {new Date(lastPush.completedAt).toLocaleString()}
                  </span>
                )}
              </div>
            )}

            <div className="space-y-3">
              {Array.from(byEmployee.values()).map((group) => (
                <div key={group.name + group.items[0].vendorEmployeeId} className="p-3 border rounded-lg">
                  <p className="font-medium mb-2">{group.name}</p>
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      const badge = ACTION_BADGES[item.action];
                      return (
                        <div
                          key={item.lineOfCoverage}
                          className="grid grid-cols-[minmax(120px,1fr)_auto_auto] items-center gap-3 text-sm"
                        >
                          <span>
                            {preview?.lineLabels[item.lineOfCoverage] ?? item.lineOfCoverage}
                            {item.desired?.assumedPayFrequency && (
                              <span className="text-muted-foreground"> (biweekly assumed — no pay group)</span>
                            )}
                          </span>
                          <span className="flex items-center gap-1.5 text-muted-foreground tabular-nums">
                            {item.action !== "create" && (
                              <>
                                <span>
                                  {money(item.current?.employeeDeduction)} EE / {money(item.current?.companyContribution)} ER
                                </span>
                                {item.action !== "none" && <ArrowRight className="h-3 w-3" />}
                              </>
                            )}
                            {item.action !== "none" && item.action !== "remove" && (
                              <span className="text-foreground">
                                {money(item.desired?.employeeDeduction)} EE / {money(item.desired?.companyContribution)} ER
                              </span>
                            )}
                          </span>
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {unlinkedSkips.length > 0 && (
              <p className="text-sm text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
                {unlinkedSkips.length} enrolled employee{unlinkedSkips.length === 1 ? " is" : "s are"} not
                linked to {connection.vendor.name} and will be skipped — resolve them in the payroll
                census above to include them.
              </p>
            )}
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Push deductions to {connection.vendor.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This updates payroll deductions for linked employees: {summary?.create ?? 0} new,{" "}
              {summary?.update ?? 0} changed, {summary?.remove ?? 0} removed. Amounts apply to every
              upcoming payroll until changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => pushMutation.mutate()}>Push</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
